import { useCallback } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import type { QuickSearchResponseData } from '../../common/types.tsx';
import {
	BOARD_LIMIT,
	FILTER_LIMIT,
	ISSUE_LIMIT,
	PLAN_LIMIT,
	PROJECT_LIMIT,
} from '../../common/ui/constants.tsx';

const EXPERIENCE = 'command-palette-jira-integration';

// limit of results per scope (can only choose 1 limit, so we are using the max limit)
// we are also adding 1 to this so we know if there are more items than the limit (vs there is exactly the limit)
const LIMIT = Math.max(ISSUE_LIMIT, PROJECT_LIMIT, BOARD_LIMIT, FILTER_LIMIT, PLAN_LIMIT) + 1;

export const useQuickSearch = () => {
	const { cloudId } = useTenantContext();

	const fetch = useCallback(
		async (
			query: string,
			sessionId: string,
			signal?: AbortSignal,
		): Promise<QuickSearchResponseData> => {
			try {
				const data = await performPostRequest('/gateway/api/xpsearch-aggregator/quicksearch/v1', {
					body: JSON.stringify({
						cloudId,
						scopes: ['jira.issue', 'jira.board', 'jira.project', 'jira.filter', 'jira.plan'],
						query,
						experience: EXPERIENCE,
						searchSession: {
							sessionId,
							referrerId: null,
						},
						limit: LIMIT,
					}),
					signal,
				});
				return data;
			} catch (error) {
				fireErrorAnalytics({
					error: error instanceof Error ? error : new Error('Error not identified'),
					meta: {
						id: 'fetchRecentlyViewedIssuesService',
						packageName: 'jiraCommandPaletteQuickSearch',
						teamName: 'deliveroo',
					},
				});
				throw error;
			}
		},
		[cloudId],
	);

	return {
		fetch,
	};
};
